import React, { useContext } from "react";
import { clsx } from "clsx";
import dynamic from "next/dynamic";

import { GridLayout } from "~components/shared/grid/gridLayout";
import { GridZone, LazyZone, Zone } from "~components/shared/zones";
import { PageContext } from "~context/PageContext";
import useRedux from "~redux/hooks";
import type { CurationZone } from "~types/types";

import zoneStyles from "./zone.module.css";
import zonesStyles from "./zones.module.css";

const PreviewPage = dynamic(() =>
    import("~components/shared/preview").then((module) => module.PreviewPage)
);

export interface ZonesProps {
    className?: string;
    initialZones: CurationZone[];
    lazyZones?: CurationZone[];
    showPreviewTools?: boolean;
}

export const Zones = ({
    className = "",
    initialZones,
    lazyZones,
    showPreviewTools,
}: ZonesProps) => {
    const { useGrid } = useContext(PageContext);
    const { useAppSelector } = useRedux();
    const { preview, pageId } = useAppSelector((state) => state.pageData);

    const [zonePageId] = pageId.split("-");
    const pageClasses = clsx(className, {
        [zonesStyles.zones]: !useGrid,
        [zoneStyles[zonePageId]]: !useGrid,
    });

    let pageContent = (
        <>
            {initialZones.map((zone, index) =>
                useGrid ? (
                    <GridZone key={index} preview={preview} zone={zone} />
                ) : (
                    <Zone key={index} preview={preview} zone={zone} />
                )
            )}
            {lazyZones &&
                lazyZones.map((zone, index) => (
                    <LazyZone
                        key={index}
                        preview={preview}
                        zone={zone}
                        useGrid={useGrid}
                    />
                ))}
        </>
    );

    if (useGrid) {
        pageContent = <GridLayout>{pageContent}</GridLayout>;
    }

    if (preview) {
        return (
            <PreviewPage
                className={pageClasses}
                showPreviewTools={showPreviewTools}
            >
                {pageContent}
            </PreviewPage>
        );
    }

    return <div className={pageClasses}>{pageContent}</div>;
};
